/* eslint-disable  */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BgpNeighbor, bgpNeighborsColumns as defaultBgpNeighborsColumns } from './BgpDetailsData';
import GenericCard from '@ui-components/Card/GenericCard';
import GenericButton from '@ui-components/Button/GenericButton';
import GenericInput from '@ui-components/Input/GenericInput';
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable';
import BgpModal from './BgpModal/BgpModal';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/Icon_Edit.svg';
import { ReactComponent as IconDelete } from '../../../../shared/assets/Icons/Icon_Delete.svg';
import { getReflectorBgpNeighbors, getClusterBgpNeighbors, updateReflectorBgpConfig, deleteReflectorBgpNeighbor, ReflectorBgpConfig, getReflectorBgpConfig } from '../request/request';
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { getBgpConfig, createBgpConfig, updateBgpConfig } from '../Cluster/request/request';
import { AxiosError } from 'axios';
import EmptyItems from '@shared/components/EmptyItems';

// No-op function for optional callbacks
const noop = () => {};

interface BgpDetailsPageProps {
  config: any;
  reflectorId?: string;
  clusterId?: string;
  getAccessTokenSilently: () => Promise<string>;
  showReflector2?: boolean;
  routerId2?: string;
  onRouterId2Change?: (val: string) => void;
  context?: 'reflector' | 'cluster';
}


// Helper to build ctxrConfigArray for cluster
function buildCtxrConfigArray(reflectors: number[], interfaceIds: number[]) {
  if (!Array.isArray(reflectors) || !Array.isArray(interfaceIds) || reflectors.length !== interfaceIds.length) return [];
  return reflectors.map((reflectorId, idx) => ({
    reflectorInstanceId: reflectorId,
    interfaceAddressId: interfaceIds[idx]
  }));
}

const BgpDetailsPage: React.FC<BgpDetailsPageProps> = ({
  config,
  reflectorId,
  clusterId,
  getAccessTokenSilently,
  showReflector2 = false,
  routerId2 = '',
  onRouterId2Change,
  context = 'reflector',
}) => {
  const navigate = useNavigate();
  const [localAs, setLocalAs] = useState(config?.localAs || '');
  const [routerId, setRouterId] = useState(config?.routerId || '');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);
  const [neighbors, setNeighbors] = useState<BgpNeighbor[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasNoInitialConfig, setHasNoInitialConfig] = useState(false);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    variant: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    description: "",
    variant: "success"
  });

  const clusterReflectors = config?.clusterReflectors || [];
  const clusterReflectorNames = config?.clusterReflectorNames || [];
  const clusterInterfaceIds = config?.clusterInterfaceIds || [];
  const reflectorName = context === 'reflector' 
    ? (config?.reflectorInstance?.name || (reflectorId ? `Reflector-${reflectorId}` : 'Reflector'))
    : (reflectorId ? `Reflector-${reflectorId}` : 'Reflector');
  const ctxrConfigArray = context === 'cluster' ? buildCtxrConfigArray(clusterReflectors, clusterInterfaceIds) : undefined;

  // Fetch BGP config on component mount
  useEffect(() => {
    const fetchBgpConfig = async () => {
      if (!clusterId && !reflectorId) return;
      
      try {
        const token = await getAccessTokenSilently();
        if (context === 'cluster' && clusterId) {
          const response = await getBgpConfig(token, Number(clusterId));
          if (response.data) {
            setLocalAs(response.data.localAs?.toString() || '');
            setRouterId(response.data.routerId1 || '');
            if (onRouterId2Change) {
              onRouterId2Change(response.data.routerId2 || '');
            }
            setHasNoInitialConfig(false);
          } else {
            // Initialize with empty strings if no config exists
            setLocalAs('');
            setRouterId('');
            if (onRouterId2Change) {
              onRouterId2Change('');
            }
            setHasNoInitialConfig(true);
          }
        } else if (reflectorId) {
          const response = await getReflectorBgpConfig(token, Number(reflectorId));
          if (response.data) {
            setLocalAs(response.data.localAs?.toString() || '');
            setRouterId(response.data.routerId || '');
            setHasNoInitialConfig(false);
          } else {
            // Initialize with empty strings if no config exists
            setLocalAs('');
            setRouterId('');
            setHasNoInitialConfig(true);
          }
        }
      } catch (err) {
        console.error('Error fetching BGP config:', err);
        // Initialize with empty strings on error
        setLocalAs('');
        setRouterId('');
        if (onRouterId2Change) {
          onRouterId2Change('');
        }
        setHasNoInitialConfig(true);
      }
    };

    fetchBgpConfig();
  }, [clusterId, reflectorId, context, getAccessTokenSilently, onRouterId2Change]);

  const fetchNeighbors = async () => {
    const id = context === 'reflector' ? reflectorId : clusterId;
    if (!id) return;

    setTableLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const response = context === 'reflector' 
        ? await getReflectorBgpNeighbors(token, Number(id))
        : await getClusterBgpNeighbors(token, Number(id));

      let mappedNeighbors = [];
      if (context === 'cluster') {
        // For cluster mode, the data is in response.data.data
        mappedNeighbors = (response.data.data || []).map((neighbor: any) => {
          const mappedNeighbor = {
            ...neighbor,
            id: neighbor.neighborIds?.[0] || neighbor.id, // Use first neighbor ID or fallback
            interfaceAddress: neighbor.localIPsString || neighbor.localIPs?.join(', ') || '-',
            // Map the fields to match the table structure
            neighborAddress: neighbor.neighborAddress,
            peerAs: neighbor.peerAs?.toString() || '',
            localAs: neighbor.localAs?.toString() || '',
            operatingMode: neighbor.operatingMode?.toUpperCase() || 'ENTERPRISE',
            status: 'Active', // Default status since it's not in the response
          };
          return mappedNeighbor;
        });
      } else {
        mappedNeighbors = (response.data || []).map((neighbor: any) => ({
          ...neighbor,
          id: neighbor.id,
          interfaceAddress:
            typeof neighbor.interfaceAddress === 'object' && neighbor.interfaceAddress !== null
              ? neighbor.interfaceAddress.ipAddress
              : neighbor.interfaceAddress || '-',
        }));
      }

      setNeighbors(mappedNeighbors);
    } catch (err) {
      // Don't set error if we have no initial config - allow user to create one
      if (!hasNoInitialConfig) {
        setError('Failed to load BGP neighbors');
      }
      console.error('Error fetching BGP neighbors:', err);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchNeighbors();
  }, [reflectorId, clusterId, context]);

  const handleDeleteBgpNeighbor = async (neighborId: number) => {

    setIsDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      await deleteReflectorBgpNeighbor(token, neighborId);
      
      // Show success alert first
      setAlertState({
        isOpen: true,
        title: "Success",
        description: "BGP neighbor has been deleted successfully.",
        variant: "success"
      });

      // Wait for alert to be shown before refreshing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Then refresh the table
      await fetchNeighbors();
    } catch (error) {
      console.error('Error deleting BGP neighbor:', error);
      // Show error alert
      setAlertState({
        isOpen: true,
        title: "Error",
        description: "Failed to delete BGP neighbor. Please try again.",
        variant: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Use passed columns or default, but override Actions column here:
  const tableColumns: GenericTableColumn<BgpNeighbor>[] = [
    ...((defaultBgpNeighborsColumns).filter(col => col.header !== 'Actions')),
    {
      header: 'Actions',
      accessor: undefined,
      renderCell: (row: any) => (
        <div className="flex items-center gap-2">
          <GenericButton
            isIconOnly
            icon={<IconEdit className="w-8 h-8" />}
            variant="secondary"
            shape="filled"
            size="sm"
            aria-label="Edit"
            onClick={() => {
              setEditMode(true);
              setEditRow(row);
              setIsAddModalOpen(true);
            }}
          />
          <GenericButton
            isIconOnly
            icon={<IconDelete className="w-6 h-6" />}
            variant="danger"
            shape="filled"
            size="sm"
            aria-label="Delete"
            onClick={() => {
              handleDeleteBgpNeighbor(row.id);
            }}
            disabled={isDeleting}
          />
        </div>
      ),
    },
  ];

  const handleEditBgpNeighbor = async (updatedData: any) => {
    setIsAddModalOpen(false);
    setEditRow(null);
    setEditMode(false);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    fetchNeighbors();
  };

  const handleAddBgpNeighbor = async (neighbor: any) => {
    setIsAddModalOpen(false);
    setEditRow(null);
    setEditMode(false);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    fetchNeighbors();
  };

  const handleSaveConfiguration = async () => {
    setIsSaving(true);
    try {
      const token = await getAccessTokenSilently();
      
      if (context === 'cluster' && clusterId) {
        // Cluster mode: use cluster-specific endpoints
        const payload = {
          localAs: Number(localAs),
          routerId1: routerId,
          routerId2: routerId2
        };


        try {
          // Try to update first
          await updateBgpConfig(token, Number(clusterId), payload);
        } catch (error) {
          // If update fails (e.g., config doesn't exist), try to create
          if (error instanceof AxiosError && error.response?.status === 404) {
            await createBgpConfig(token, Number(clusterId), payload);
          } else {
            throw error;
          }
        }
      } else if (reflectorId) {
        // Reflector mode: use reflector-specific endpoint
        const payload: ReflectorBgpConfig = {
          localAs: Number(localAs),
          routerId: routerId
        };

        await updateReflectorBgpConfig(token, Number(reflectorId), payload);
      }
      
      // Show success alert
      setAlertState({
        isOpen: true,
        title: "Success",
        description: "BGP configuration has been updated successfully.",
        variant: "success"
      });

      // Refresh the neighbors list
      await fetchNeighbors();
    } catch (error) {
      console.error('Error updating BGP configuration:', error);
      // Show error alert
      setAlertState({
        isOpen: true,
        title: "Error",
        description: "Failed to update BGP configuration. Please try again.",
        variant: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  const handleRouterIdClick = (reflectorId: string) => {
    if (reflectorId) {
      navigate(`/reflector-v2/management/bgp-reflectors/details/${reflectorId}`);
    }
  };

  // Only show EmptyItems if there's a real error, not when there's no initial config
  if (error && !hasNoInitialConfig) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      {isSaving || isDeleting ? (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
          <h2 className="text-xl font-semibold mb-4 text-white">
            {isSaving ? "Saving configuration..." : "Deleting BGP neighbor..."}
          </h2>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
        </div>
      ) : null}
      <GenericCard isCollapsible={false}>
        <div className="flex flex-col gap-8 p-6">
          {/* Global/Cluster BGP Configuration */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Global BGP Configuration</h2>
              <GenericButton 
                label="Save Configuration" 
                variant="brand" 
                size="md" 
                onClick={handleSaveConfiguration}
                disabled={!localAs || !routerId || isSaving}
              />
            </div>
            {showReflector2 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={setLocalAs}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">Local Autonomous System number</div>
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(clusterReflectors[0]?.toString() || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!clusterReflectors[0]}
                        >
                          {clusterReflectorNames[0] || 'Reflector-1'}
                        </button>
                        )
                      </span>
                    }
                    value={routerId}
                    onChange={setRouterId}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">BGP Router ID for Reflector-1</div>
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(clusterReflectors[1]?.toString() || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!clusterReflectors[1]}
                        >
                          {clusterReflectorNames[1] || 'Reflector-2'}
                        </button>
                        )
                      </span>
                    }
                    value={routerId2}
                    onChange={onRouterId2Change ? onRouterId2Change : noop}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">BGP Router ID for Reflector-2</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={setLocalAs}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">Local Autonomous System number</div>
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(reflectorId || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!reflectorId}
                        >
                          {reflectorName}
                        </button>
                        )
                      </span>
                    }
                    value={routerId}
                    onChange={setRouterId}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">BGP Router ID (must be a valid IPv4 address)</div>
                </div>
              </div>
            )}
          </div>

          {/* BGP Neighbors */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">BGP Neighbors</h2>
              <GenericButton
                label="+ Add BGP Neighbor"
                variant="brand"
                size="md"
                onClick={() => {
                  setEditMode(false);
                  setEditRow(null);
                  setIsAddModalOpen(true);
                }}
              />
            </div>
            {tableLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-500" />
              </div>
            ) : (
              <GenericTable
                columns={tableColumns}
                data={neighbors}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                tableClassName="bg-white"
              />
            )}
          </div>
        </div>
      </GenericCard>
      {/* Add/Edit Modal */}
      <BgpModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={editMode ? handleEditBgpNeighbor : handleAddBgpNeighbor}
        mode={editMode ? "edit" : "add"}
        initialValues={editRow || {}}
        reflectorId={reflectorId ? Number(reflectorId) : undefined}
        getAccessTokenSilently={getAccessTokenSilently}
        context={context}
        clusterId={clusterId ? Number(clusterId) : undefined}
        clusterReflectors={clusterReflectors}
        clusterInterfaceIds={clusterInterfaceIds}
      />

      {/* Alert for configuration updates */}
      <GenericAlert
        isOpen={alertState.isOpen}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alertState.title}
        description={alertState.description}
        confirmLabel="OK"
        variant={alertState.variant}
      />
    </div>
  );
};

export default BgpDetailsPage;
